import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { CapacityState, InterfaceVerdict } from './useDeviceTrafficSeries';

const backendApi = config.backendApi;

/**
 * One port's window as a row of numbers.
 *
 * Counts, not rates — except the two that are explicitly peaks. A `null` count
 * means the counter reset or never reported; it is never zero, which is a
 * reading and a different fact.
 */
export interface InterfaceSummary {
  /** The interface (`device_interfaces.id`): the key (gateway#102). */
  interfaceId: string;
  interfaceName: string;
  /** The ifIndex it carries now, an attribute. */
  ifindex: string | null;
  operStatus: string | null;
  flaps: number;
  errorsIn: number | null;
  errorsOut: number | null;
  discardsIn: number | null;
  discardsOut: number | null;
  unknownProtosIn: number | null;
  packetsIn: number | null;
  nonUnicastIn: number | null;
  /** Peak rate over the window, from the store's rate pipeline. */
  bitsIn: number | null;
  bitsOut: number | null;
  utilization: number | null;
  capacityBits: number | null;
  capacityState: CapacityState;
  /** Some counter restarted. Its count is absent rather than repaired. */
  resetInRange: boolean;
  /**
   * Kept out of the ranking, though still a row. True for a LAG member, whose
   * aggregate already counts it, and for a logical unit, which is another view
   * of a port counted elsewhere. Two different reasons — read `role` to label,
   * this only to filter.
   */
  aggregated: boolean;
  /** What this row is: `physical`, `aggregate`, `unit` or `standalone`. */
  role: 'physical' | 'aggregate' | 'unit' | 'standalone';
  /** For a unit, the interface it folds under. */
  baseInterfaceId: string | null;
  /** For a LAG member, the aggregate that already counts it. */
  aggregateInterfaceId: string | null;
  /**
   * Whether the device reports a physical connector — `ifConnectorPresent`.
   *
   * Three states. `null` means the device does not report it, which is not
   * `false`: one fixture vendor stays silent about the column and has real ports
   * like any other, so a port list must not require a `true` to show a row.
   *
   * Not the empty-cage signal — that is `operStatus === 'notPresent'`.
   */
  connectorPresent: boolean | null;
  /** IANAifType name — `ethernetCsmacd`, `ieee8023adLag`, `tunnel`, and so on. */
  interfaceType: string | null;
  /** Which column put this row where it is. */
  reason: string;
}

export interface DeviceVerdict {
  sentence: string;
  severity: 'critical' | 'warning' | 'none';
  worstInterface: string | null;
  busiestInterface: string | null;
  portsWithFaults: number;
  portsFlapping: number;
  /** Every port less the LAG members. */
  portsRanked: number;
  portsTotal: number;
}

export interface DeviceTrafficSummary {
  start: number;
  end: number;
  /** Worst first. Ranking is server-side; a caller may cap, not reorder. */
  interfaces: InterfaceSummary[];
  verdict: DeviceVerdict;
}

const fetchSummary = async (deviceId: string, start: number, end: number): Promise<DeviceTrafficSummary> => {
  const response = await axios.get<DeviceTrafficSummary>(
    `${backendApi}/api/devices/${deviceId}/traffic-summary?start=${String(start)}&end=${String(end)}`,
    { withCredentials: true },
  );
  return response.data;
};

/**
 * Every port on one device, as numbers rather than shapes.
 *
 * A different endpoint from the series one because it answers a different
 * question at a different price: it reads the counters' endpoints, so its cost
 * is set by how many ports a device has rather than by how long the window is —
 * 47 KB at both 24 hours and 7 days for a 125-port switch, against 8.8 MB for
 * that device's full series at 24 hours alone.
 *
 * `enabled` is a real parameter rather than a formality: the node panel's
 * traffic section is collapsed on first open, and a reader walking a topology
 * clicks a dozen nodes. None of those clicks should fetch this.
 */
export function useDeviceTrafficSummary(
  deviceId: string | undefined,
  bounds: { start: number; end: number } | null,
  enabled = true,
) {
  const contextReady = useActiveContextReady();

  return useQuery<DeviceTrafficSummary>({
    queryKey: ['device-traffic-summary', deviceId, bounds],
    queryFn: () => {
      if (!deviceId || !bounds) {
        throw new Error('deviceId and a window are required to fetch a traffic summary.');
      }
      return fetchSummary(deviceId, bounds.start, bounds.end);
    },
    enabled: enabled && !!deviceId && !!bounds && contextReady,
    staleTime: 30_000,
  });
}

/** One end of a link, and the verdict that end would get on its own. */
export interface LinkEnd {
  deviceId: string;
  deviceName: string;
  /** The row these numbers came from — the base carrying the wire. */
  interfaceId: string;
  interfaceName: string;
  /** Its ifIndex, an attribute. */
  ifindex: string | null;
  /**
   * What topology named this end, present only when it is a different row than
   * the one counted.
   *
   * LLDP reports the logical unit, because the unit is what runs the protocol,
   * while the wire's bytes sit on the base beneath it — every Juniper link
   * endpoint resolves this way. Rendering the counted row under a header naming
   * the other one would be its own small lie, so the caption says both.
   */
  portName: string | null;
  verdict: InterfaceVerdict;
}

export interface LinkVerdict {
  start: number;
  end: number;
  rule: string;
  sentence: string;
  severity: 'critical' | 'warning' | 'none';
  /**
   * Named when only one end is monitored — or readable — so the surface can say
   * what is missing instead of drawing an empty second column.
   */
  unmonitoredEnd: string | null;
  /**
   * Bytes one end sent that the other never counted, when the gap clears a band
   * derived from this link's own frame mix and polling offset. Null on the
   * overwhelming majority of links, and a note under the verdict rather than a
   * verdict of its own — it answers one narrow question.
   */
  unaccounted: string | null;
  /** One or two, source then target. */
  ends: LinkEnd[];
}

const fetchLinkVerdict = async (connectionId: string, start: number, end: number): Promise<LinkVerdict | null> => {
  try {
    const response = await axios.get<LinkVerdict>(
      `${backendApi}/api/connections/${connectionId}/traffic-verdict?start=${String(start)}&end=${String(end)}`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    // A 404 means the link has no end to read: it is gone (a link the map drew
    // from a forwarding table whose MAC has since moved, or a forgotten one), or
    // neither end is monitored. Not a failure, and not one to retry.
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/**
 * What a wire is doing, from both ends at once.
 *
 * Only the sentence needs both ends simultaneously — the charts come from the
 * per-device series endpoint narrowed to each port — so this response is small
 * and the panel's two columns keep their own caches. `null` when the link has
 * no end to read.
 */
export function useLinkTrafficVerdict(
  connectionId: string | undefined,
  bounds: { start: number; end: number } | null,
) {
  const contextReady = useActiveContextReady();

  return useQuery<LinkVerdict | null>({
    queryKey: ['link-traffic-verdict', connectionId, bounds],
    queryFn: () => {
      if (!connectionId || !bounds) {
        throw new Error('connectionId and a window are required to fetch a link verdict.');
      }
      return fetchLinkVerdict(connectionId, bounds.start, bounds.end);
    },
    enabled: !!connectionId && !!bounds && contextReady,
    staleTime: 30_000,
  });
}
